import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useDeepSeek } from "./hooks/useDeepSeek";
import DeepSeekSidebar from "./components/DeepSeekSidebar";
import DeepSeekStats from "./components/DeepSeekStats";
import DeepSeekChatWindow from "./components/DeepSeekChatWindow";
import DeepSeekConvInfoModal from "./components/DeepSeekConvInfoModal";
import "./DeepSeek.css";

const DeepSeekAdmin = (props) => {
    const { t, match } = props;
    const {
        loading,
        loadingHistory,
        balance,
        stats,
        groupedConversations,
        users,
        schools,
        selectedUser,
        selectedConversationId,
        setSelectedConversationId,
        sidebarSearch,
        setSidebarSearch,
        selectUser,
        clearSelectedUser,
        filters,
        applyFilters
    } = useDeepSeek(t, match);

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [showConvInfoModal, setShowConvInfoModal] = useState(false);
    const [chatViewMode, setChatViewMode] = useState("convList");

    // Reset showInfo when conversation or user changes
    useEffect(() => {
        setShowConvInfoModal(false);
        setChatViewMode("convList");
    }, [selectedUser, selectedConversationId]);

    const activeConv = groupedConversations.find(c => c.id === selectedConversationId) || groupedConversations[0];

    return (
        <>
            <Helmet>
                <title>
                    {t('Pages.DeepSeekAdmin.title') || "إدارة ديب سيك والذكاء الاصطناعي"} | {t('alarabeyya')}
                </title>
            </Helmet>

            <div className="deepseek-page-wrapper">
                <div className={`deepseek-container ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
                    <DeepSeekSidebar
                        users={users}
                        schools={schools}
                        filters={filters}
                        onApplyFilters={applyFilters}
                        selectedUser={selectedUser}
                        sidebarSearch={sidebarSearch}
                        setSidebarSearch={setSidebarSearch}
                        onSelectUser={selectUser}
                        onClearSelected={clearSelectedUser}
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                        t={t}
                    />

                    <div className="deepseek-main-chat">
                        {/* Header area for selected user / general stats */}
                        <div className="deepseek-chat-header">
                            <div className="d-flex align-items-center gap-2">
                                {/* Button to show sidebar/conversations on small screen */}
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline-secondary d-md-none ml-2"
                                    onClick={() => setSidebarOpen(true)}
                                    title={t('Pages.DeepSeekAdmin.showConversations') || "المحادثات والبحث"}
                                    style={{ width: 'auto' }}
                                >
                                    <i className="fal fa-comments"></i>
                                </button>

                                {selectedUser ? (
                                    <div className="deepseek-chat-header-info text-right">
                                        <div>
                                            <h5 className="deepseek-active-name">{selectedUser.Name}</h5>
                                            <p className="deepseek-active-status d-md-block d-none">{selectedUser.Username}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="deepseek-chat-header-info text-right">
                                        <div>
                                            <h5 className="deepseek-active-name">{t('Pages.DeepSeekAdmin.allStats') || "إحصائيات الاستهلاك العامة"}</h5>
                                            <p className="deepseek-active-status">{t('Pages.DeepSeekAdmin.usageOverTime') || "الاستهلاك اليومي خلال آخر 30 يوم"}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Wallet balance banner in header with custom modern view */}
                            {balance && !selectedUser && (
                                (() => {
                                    const isAvailable = balance.is_available ?? false;
                                    const balanceInfos = balance.balance_infos || [];
                                    const usdBalance = balanceInfos.find(b => b.currency === "USD") || { total_balance: "0.00" };
                                    return (
                                        <div className={`deepseek-header-balance-widget ${isAvailable ? 'available' : 'unavailable'}`} title={isAvailable ? t('Pages.DeepSeekAdmin.available') : t('Pages.DeepSeekAdmin.unavailable')}>
                                            <div className="balance-info-pill">
                                                <i className="fal fa-wallet wallet-icon"></i>
                                                <div className="balance-text-group">
                                                    <span className="balance-lbl">{t('Pages.DeepSeekAdmin.balanceCard') || "رصيد ديب سيك API"}</span>
                                                    <span className="balance-val">${parseFloat(usdBalance.total_balance).toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <span className="status-dot"></span>
                                        </div>
                                    );
                                })()
                            )}

                            {selectedUser && (
                                <div className="d-flex align-items-center gap-2 position-relative">
                                    {activeConv && (
                                        <button
                                            className={`btn btn-xs ${showConvInfoModal ? 'btn-primary' : 'btn-outline-secondary'} ml-2`}
                                            onClick={() => setShowConvInfoModal(true)}
                                            title={t('Pages.DeepSeekAdmin.infoTitle') || "تفاصيل الاستهلاك"}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px', width: 'auto' }}
                                        >
                                            <i className="fal fa-info-circle"></i>
                                            <span className=" d-md-block d-none">{t('Pages.DeepSeekAdmin.info') || "معلومات"}</span>
                                        </button>
                                    )}

                                    {/* Mobile: Back to conversations list button */}
                                    {chatViewMode === "chat" && (
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-outline-secondary d-md-none"
                                            onClick={() => setChatViewMode("convList")}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px', width: 'auto' }}
                                        >
                                            <i className="fal fa-chevron-left"></i>
                                            <span className=" d-md-block d-none">{t('Pages.DeepSeekAdmin.backToConversations') || "العودة للمحادثات"}</span>
                                        </button>
                                    )}

                                    {/* Universal Back to Stats button: No duplicates, width auto, returns to dashboard on small screen */}
                                    {chatViewMode !== "chat" && (
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-outline-secondary deepseek-back-btn"
                                            onClick={() => {
                                                clearSelectedUser();
                                                // On small screen, close sidebar to show stats dashboard
                                                if (window.innerWidth < 768) {
                                                    setSidebarOpen(false);
                                                } else {
                                                    setSidebarOpen(true);
                                                }
                                            }}
                                            style={{ display: 'flex', alignItems: 'center', gap: '4px', width: 'auto' }}
                                        >
                                            <i className="fal fa-chevron-left"></i>
                                            <span className=" d-md-block d-none">{t('Pages.DeepSeekAdmin.backToStats') || "العودة للإحصائيات"}</span>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Content Area */}
                        {selectedUser ? (
                            <DeepSeekChatWindow
                                selectedUser={selectedUser}
                                loadingHistory={loadingHistory}
                                groupedConversations={groupedConversations}
                                selectedConversationId={selectedConversationId}
                                setSelectedConversationId={setSelectedConversationId}
                                chatViewMode={chatViewMode}
                                setChatViewMode={setChatViewMode}
                                t={t}
                            />
                        ) : (
                            <DeepSeekStats
                                stats={stats}
                                loading={loading}
                                onSelectUser={selectUser}
                                t={t}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Conversation Details Modal */}
            {activeConv && (
                <DeepSeekConvInfoModal
                    show={showConvInfoModal}
                    onHide={() => setShowConvInfoModal(false)}
                    activeConv={activeConv}
                    selectedUser={selectedUser}
                    t={t}
                />
            )}
        </>
    );
};

export default withTranslation('common')(DeepSeekAdmin);
